/* eslint-disable indent */
/** @typedef {import('headlight-configurable-application').Pict} Pict */

const PictSectionInputExtension = require('pict-section-form').PictInputExtensionProvider;

class CustomInputHandler extends PictSectionInputExtension
{
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		/** @type {Pict} */
		this.pict;
	}

	/**
	 * Updates the rows attribute of the textarea based on the content.
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be used for calculating rows.
	 * @param {number} [pRowIndex] - Row index when the textarea is a tabular cell; omit for standard inputs.
	 */
	updateTextAreaRows(pView, pInput, pValue, pRowIndex)
	{
		// Tabular textareas render with a per-row id suffix ("{RawHTMLID}-{rowIndex}",
		// per the InputType-TextArea tabular template); standard inputs use the bare
		// RawHTMLID. Match the suffix here or the lookup misses every tabular cell.
		const tmpHTMLID = (pRowIndex != null) ? `${pInput.Macro.RawHTMLID}-${pRowIndex}` : pInput.Macro.RawHTMLID;
		const textarea = document.getElementById(tmpHTMLID);
		if (!textarea)
		{
			// Expected during early render: a marshal can fire before a section's
			// (or row's) textarea DOM exists yet. Auto-grow re-applies on the next
			// marshal once it's present, so this is a benign skip — trace, not error.
			this.pict.log.trace(`[TextAreaProvider] Textarea [${tmpHTMLID}] not in DOM yet; skipping auto-grow.`);
			return;
		}
		const scrollHeight = textarea.scrollHeight;
		const minHeight = 50; // Minimum height in pixels (about 2 rows)
		const maxHeight = 300; // Maximum height in pixels (about 12 rows)
		const newHeight = Math.max(minHeight, Math.min(scrollHeight, maxHeight));
		textarea.setAttribute('style', `height: ${newHeight}px;`);
	}

	/**
	 * Marshals data to the form for the given input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pRow - The row object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value to be marshaled.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {boolean} - Returns true if the value is successfully marshaled to the form, otherwise false.
	 */
	onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID)
	{
		this.updateTextAreaRows(pView, pInput, pValue);
		return super.onDataMarshalToForm(pView, pGroup, pRow, pInput, pValue, pHTMLSelector, pTransactionGUID);
	}

	/**
	 * Marshals data to a form in tabular format.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pGroup - The group object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value parameter.
	 * @param {string} pHTMLSelector - The HTML selector parameter.
	 * @param {number} pRowIndex - The row index parameter.
	 * @param {string} pTransactionGUID - The transaction GUID.
	 * @return {any} - The result of the data marshaling.
	 */
	onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID)
	{
		this.updateTextAreaRows(pView, pInput, pValue, pRowIndex);
		return super.onDataMarshalToFormTabular(pView, pGroup, pInput, pValue, pHTMLSelector, pRowIndex, pTransactionGUID);
	}

	/**
	 * Handles the data request event for a select input in the PictProviderInputSelect class.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector object.
	 * @return {any} - The result of the onDataRequest method.
	 */
	onDataRequest(pView, pInput, pValue, pHTMLSelector)
	{
		return super.onDataRequest(pView, pInput, pValue, pHTMLSelector);
	}

	/**
	 * Handles the data request event for a tabular input.
	 *
	 * @param {Object} pView - The view object.
	 * @param {Object} pInput - The input object.
	 * @param {any} pValue - The value object.
	 * @param {string} pHTMLSelector - The HTML selector.
	 * @param {number} pRowIndex - The row index.
	 * @return {any} - The result of the data request.
	 */
	onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex)
	{
		return super.onDataRequestTabular(pView, pInput, pValue, pHTMLSelector, pRowIndex);
	}
}

module.exports = CustomInputHandler;
